import { InfoBox } from '@common/components';
import { OptionSelector } from '@common/components/OptionSelector';
import { useTranslations } from '@lang';
import { FC, useEffect } from 'react';
import {
  Controller,
  FieldArrayPath,
  get,
  Path,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { ConfigurationGroupOptionType } from 'shared-types/SpaPackage';
import { SpaPackageFormValues } from '../../../../../SpaPackageForm.schema';
import { GroupError } from '../GroupError';
import { ConfigurationGroupHeading } from './ConfigurationGroupHeading';
import { ConfigurationGroupWrapper } from './ConfigurationGroupHeading.styles';

type SelectionData = {
  configurationGroupId: number;
  optionId: number;
  isSelected: boolean;
  timeslot: { startTime: string; endTime: string } | null;
};

type ConfigurationGroupProps = {
  fieldName: string;
  options: ConfigurationGroupOptionType[];
  minimumSelections?: number;
  maximumSelections?: number;
  supportsSelections?: boolean;
  requireTimeSlots: boolean;
  configurationGroupId: number;
  index: number;
};

export const ConfigurationGroup: FC<ConfigurationGroupProps> = ({
  fieldName,
  options,
  minimumSelections,
  maximumSelections,
  supportsSelections,
  requireTimeSlots,
  configurationGroupId,
  index,
}) => {
  const { t } = useTranslations();
  const { control, formState, trigger } = useFormContext<SpaPackageFormValues>();
  const { fields, replace } = useFieldArray({
    control,
    name: fieldName as FieldArrayPath<SpaPackageFormValues>,
  });
  const liveFieldValues = useWatch({ control, name: fieldName as Path<SpaPackageFormValues> }) as
    | SelectionData[]
    | undefined;

  useEffect(() => {
    if (options.length === 0) {
      return;
    }
    const current = liveFieldValues || [];
    const optionIds = options.map((o) => o.id);
    const currentIds = current.map((f) => f?.optionId);
    const sameSet =
      optionIds.length === currentIds.length && optionIds.every((id) => currentIds.includes(id));
    if (sameSet) {
      return;
    }
    replace(
      options.map(({ id }) => {
        const existing = current.find((f) => f?.optionId === id);
        return {
          configurationGroupId,
          optionId: id,
          isSelected: existing?.isSelected ?? false,
          timeslot: existing?.timeslot ?? null,
        } as SelectionData;
      })
    );
    trigger();
  }, [options, liveFieldValues]);

  const numberSelectedOptions = (liveFieldValues || []).filter((f) => f?.isSelected).length;

  const groupError =
    formState.submitCount > 0 ? get(formState.errors, fieldName)?.message : undefined;

  return (
    <ConfigurationGroupWrapper>
      <ConfigurationGroupHeading
        minimumSelections={minimumSelections}
        maximumSelections={maximumSelections}
        numberSelected={numberSelectedOptions}
        className={index > 0 ? 'with-top-margin' : ''}
      />
      {requireTimeSlots && <InfoBox>{t('spa.timeslotsWarning')}</InfoBox>}
      <GroupError meta={{ submitFailed: formState.submitCount > 0, error: groupError }} />
      {fields.map((fieldEntry, fieldIndex) => {
        const currentField = fieldEntry as SelectionData & { id: string };
        const { optionId } = currentField;
        const { name, timeslots } = options.find((o) => o.id === optionId) || {};

        const liveValue = liveFieldValues?.[fieldIndex];
        const isFieldSelected = liveValue?.isSelected ?? false;

        let disabled = maximumSelections
          ? numberSelectedOptions >= maximumSelections && !isFieldSelected
          : false;

        if (timeslots?.length === 0) {
          disabled = true;
        }

        return (
          <Controller
            key={fieldEntry.id}
            name={`${fieldName}.${fieldIndex}` as Path<SpaPackageFormValues>}
            control={control}
            render={({ field, fieldState }) => (
              <OptionSelector
                input={{
                  value: field.value,
                  onChange: field.onChange,
                }}
                meta={{
                  submitFailed: formState.submitCount > 0,
                  error: fieldState.error?.message,
                }}
                optionTitle={name}
                supportsSelections={supportsSelections}
                disabled={disabled}
                timeslots={timeslots}
                requireTimeSlots={requireTimeSlots}
              />
            )}
          />
        );
      })}
    </ConfigurationGroupWrapper>
  );
};
